import React from 'react'
import { Skeleton, Typography, Button, Form, Input, DatePicker, Switch, Row, Col, message } from 'antd'
import moment from 'moment'
import { FormComponent, BaseFormComponentProps } from '../../BaseComponent'
import { ImageUpload } from '../../Upload'
import TableTransfer from '../../TableTransfer'
import IEvent from '../../../interfaces/IEvent'
import IShelter from '../../../interfaces/IShelter'
import ISponsor from '../../../interfaces/ISponsor'
import { SearchRequest, EventSaveRequest }from '../../../services/api/interfaces/ApiRequest'
import slugGenerator from '../../../helpers/slug-generator.helper'
import Config from '../../../config'

type EventProps = BaseFormComponentProps & {
  uuid?: string,
  data?: IEvent,
  error: any | null,
  errorCode: number | null,

  shelters: IShelter[] | null,
  sponsors: ISponsor[] | null,

  loading: boolean,
  contentLoading: boolean,
  history: any,

  loadEvent: (args: SearchRequest) => void,
  loadShelters: (args: SearchRequest) => void,
  loadSponsors: (args: SearchRequest) => void,

  dispatchCreate: (args: EventSaveRequest) => Promise<any>,
  dispatchUpdate: (args: EventSaveRequest) => Promise<any>
}

export default class Event extends FormComponent<EventProps> {

  static defaultProps = {
    contentLoading: true
  }

  formRef: any = null

  state = {
    saving: false,
    loaded: false,
    fileList: [] as any,
    sheltersSelected: [] as string[],
    sponsorsSelected: [] as string[],
    sheltersSelection: {
      sourceSelectedKeys: [] as any,
      targetSelectedKeys: [] as any
    },
    sponsorsSelection: {
      sourceSelectedKeys: [] as any,
      targetSelectedKeys: [] as any
    }
  }

  // Transfer shelters columns
  transferSheltersColumns = {
    left: [
      {
        dataIndex: 'name',
        title: 'Name',
        searchable: true
      }
    ],
    right: [
      {
        dataIndex: 'name',
        title: 'Name'
      },
      {
        dataIndex: 'active',
        title: 'Active',
        render: (text: any, record: any) => (
          <div>
            <Form.Item
              name={['shelters', record.key, 'active']}
              valuePropName="checked"
            >
              <Switch />
            </Form.Item>
          </div>
        )
      }
    ]
  }

  // Transfer sponsors columns
  transferSponsorsColumns = {
    left: [
      {
        dataIndex: 'name',
        title: 'Name',
        searchable: true
      }
    ],
    right: [
      {
        dataIndex: 'name',
        title: 'Name'
      },
      {
        dataIndex: 'active',
        title: 'Active',
        render: (text: any, record: any) => (
          <div>
            <Form.Item
              name={['sponsors', record.key, 'active']}
              valuePropName="checked"
            >
              <Switch />
            </Form.Item>
          </div>
        )
      }
    ]
  }

  componentDidMount() {
    const { loadShelters, loadSponsors } = this.props

    // Check permissions
    this.validateAccessPermissions()

    // Load shelters
    loadShelters({
      search: {
        deleted: false
      },
      options: {
        _force_ids: true,
        limit: 9999
      }
    })

    // Load sponsors
    loadSponsors({
      search: {
        deleted: false
      },
      options: {
        _force_ids: true,
        limit: 9999
      }
    })
  }

  componentDidUpdate(prevProps: EventProps, prevState) {
    const wasLoading = prevProps.loading && !this.props.loading
    let newState = Object.assign({}, this.state)

    // Check permissions
    this.validateAccessPermissions()

    // We are loaded?
    if (!this.state.loaded && !this.props.contentLoading) {
      if (this.props.uuid && !this.props.loading && !wasLoading) {
        this.props.loadEvent({
          search: {
            uuid: this.props.uuid,
            sponsors: {
              with: {
                deleted: false,
                sponsor: {
                  with: true
                }
              }
            },
            shelters: {
              with: {
                deleted: false,
                shelter: {
                  with: true
                }
              }
            }
          },
          options: {
            _force_ids: true
          }
        })
      }

      // If our entity was loaded or if we are creating a new one, that's means that we are loaded :P
      if (wasLoading || !this.props.uuid) {
        // Setup event banner
        if (this.props.uuid && this.props.data) {
          const { banner, shelters, sponsors } = this.props.data

          if (banner && banner.length > 0) {
            newState.fileList = [{
              uid: this.props.uuid,
              url: `${Config.cdnImagesBase}organization/event/banner/AUTOx200/${banner}`,
              status: 'done'
            }]
          }

          // Event shelters
          shelters?.forEach(p => {
            if (!newState.sheltersSelected.includes(p.shelter.uuid)) {
              newState.sheltersSelected.push(p.shelter.uuid)
            }
          })

          // Event sponsors
          sponsors?.forEach(p => {
            if (!newState.sponsorsSelected.includes(p.sponsor.uuid)) {
              newState.sponsorsSelected.push(p.sponsor.uuid)
            }
          })
        }

        newState.loaded = true
      } 
    } else {
      // We are saving
      if (this.state.saving && wasLoading) {
        // Add the new event uuid to url
        if (!this.props.uuid && this.props.data) {
          this.props.history.replace(`/organization/event/${this.props.data.uuid}`)
        }

        if (!this.props.error) {
          message.success('Event saved successfully!')
        } else {
          message.error('Failed to save event. Error: ' + this.props.error)

          // Slug error
          if (5655 === this.props.errorCode) {
            this.formRef.setFields([{
              name: 'slug',
              errors: [this.props.error]
            }])
          }
        }

        newState.saving = false
      }
    }

    // Update if changed
    if (newState.loaded !== this.state.loaded) {
      this.setState(newState)
    }
  }

  onChangeField = (name, { target: { value }}) => {
    if (!this.props.uuid || name === 'slug') {
      this.formRef.setFieldsValue({
        slug: slugGenerator(value)
      })
    }
  }

  handleImageUploadChange = (fileList) => {
    this.setState({
      fileList
    })
  }

  handleShelterSelection = (targetKeys: any, direction: any, moveKeys: any) => {
    this.setState({
      sheltersSelected: targetKeys
    })
  }

  handleSponsorSelection = (targetKeys: any, direction: any, moveKeys: any) => {
    this.setState({
      sponsorsSelected: targetKeys
    })
  }

  handleSelectionChange = (transferName: string, sourceSelectedKeys: string[], targetSelectedKeys: string[]) => {
    this.setState(state => ({
      ...state,
      [transferName]: {
        sourceSelectedKeys,
        targetSelectedKeys
      }
    }))
  }

  handleSave = (values: any) => {
    const { uuid, dispatchCreate, dispatchUpdate } = this.props
    const { fileList, sheltersSelected, sponsorsSelected } = this.state

    if (fileList.length === 0) {
      message.error('Event banner is required. Please upload an image.')
      return
    }

    let payload: EventSaveRequest = {
      organization_event: {
        ...values
      },
      organization_event_shelter: sheltersSelected.map(shelterUuid => {
        return {
          shelter_uuid: shelterUuid,
          active: values.shelters[shelterUuid].active || false,
        }
      }) as any,
      organization_event_sponsor: sponsorsSelected.map(sponsorUuid => {
        return {
          sponsor_uuid: sponsorUuid,
          active: values.sponsors[sponsorUuid].active || false,
        }
      }) as any
    }

    // Fix event datetime
    payload = {
      ...payload,
      organization_event: {
        ...payload.organization_event,
        start_datetime: `${values.datetime[0].format('YYYY-MM-DD HH:mm:ss')}+00`,
        end_datetime: `${values.datetime[1].format('YYYY-MM-DD HH:mm:ss')}+00`,
      }
    }

    // Remove datetime object
    delete (payload.organization_event as any).datetime

    // Parse banner to request
    const banner = fileList[0]
    if (banner.url.includes('data:image')) {
      payload = {
        ...payload,
        organization_event: {
          ...payload.organization_event,
          banner_base64: banner.url
        }
      }
    }

    if (uuid) {
      payload = {
        ...payload,
        organization_event: {
          ...payload.organization_event,
          uuid
        }
      }
      
      dispatchUpdate(payload)
      this.setState({
        saving: true
      })
    } else {
      dispatchCreate(payload)
      this.setState({
        saving: true
      })
    }
  }

  render() {
    const { uuid, data, shelters, sponsors, loading } = this.props
    const { loaded, fileList, sheltersSelected, sponsorsSelected } = this.state

    if (!loaded) {
      return <Skeleton />
    }

    // Mapping shelters for transfer options
    let eventShelters = {} as any
    const shelterOptions = shelters?.map(shelter => {
      const selected = sheltersSelected.includes(shelter.uuid)
      const eventShelter = data?.shelters?.find(x => x.shelter.uuid === shelter.uuid)

      if (selected && eventShelter) {
        eventShelters[shelter.uuid] = eventShelter
      }

      return {
        key: shelter.uuid,
        name: shelter.name,
        active: selected && eventShelter ? eventShelter.active : false,
        chosen: selected !== undefined
      }
    }) || []

    // Mapping sponsors for transfer options
    let eventSponsors = {} as any
    const sponsorOptions = sponsors?.map(sponsor => {
      if (!sponsor.uuid) return null

      const selected = sponsorsSelected.includes(sponsor.uuid)
      const eventSponsor = data?.sponsors?.find(x => x.sponsor.uuid === sponsor.uuid)

      if (selected && eventSponsor) {
        eventSponsors[sponsor.uuid] = eventSponsor
      }

      return {
        key: sponsor.uuid,
        name: sponsor.name,
        active: selected && eventSponsor ? eventSponsor.active : false,
        chosen: selected !== undefined
      }
    }) || []

    let values:any = {
      name: '',
      description: null,
      active: false,
      search_enabled: false
    }

    if (uuid) {
      let datetime: any = null
      if (data?.start_datetime && data?.end_datetime) {
        datetime = [moment.utc(data.start_datetime), moment.utc(data.end_datetime)]
      }

      values = {
        ...data || {},
        shelters: eventShelters,
        sponsors: eventSponsors,
        datetime
      }
    }

    return (
      <Form
        name="organization_event"
        initialValues={values}
        layout="vertical"
        ref={refs => this.formRef = refs}
        onFinish={this.handleSave}
      >
        <Row gutter={24}>
          <Col xl={24} xxl={12}>
            <Row gutter={24}>
              <Col lg={24} xl={12}>
                <Form.Item
                  label="Name"
                  name="name"
                  rules={[
                    {
                      required: true,
                      message: 'Missing a name for the event'
                    }
                  ]}
                >
                  <Input onChange={(ev) => this.onChangeField('name', ev)} />
                </Form.Item>
              </Col>

              <Col lg={24} xl={12}>
                <Form.Item
                  label="Slug"
                  name="slug"
                  rules={[
                    {
                      required: true,
                      message: 'Missing a slug for the event'
                    }
                  ]}
                >
                  <Input onChange={(ev) => this.onChangeField('slug', ev)} />
                </Form.Item>
              </Col>
            </Row>

            <Typography.Title level={5}>Banner</Typography.Title>
            <ImageUpload
              name="banner"
              fileList={fileList}
              aspect={4 / 3}
              className="sponsor-logo-uploader"
              onChange={this.handleImageUploadChange}
            />
          </Col>

          <Col xl={24} xxl={12}>
            <Form.Item
              label="Description"
              name="description"
            >
              <Input.TextArea rows={4} />
            </Form.Item>

            <Row gutter={24}>
              <Col xs={24} md={12}>
                <Form.Item
                  label="Event Date"
                  name="datetime"
                  rules={[
                    {
                      required: true,
                      message: 'Please inform the starting date and ending date of the event'
                    }
                  ]}
                >
                  <DatePicker.RangePicker showTime/>
                </Form.Item>
              </Col>

              <Col xs={24} md={12}>
                <Row>
                  <Col xs={8}>
                    <Form.Item
                      label="Active"
                      name="active"
                      valuePropName="checked"
                    >
                      <Switch />
                    </Form.Item>
                  </Col>

                  <Col xs={8}>
                    <Form.Item
                      label="Searchable"
                      name="search_enabled"
                      valuePropName="checked"
                    >
                      <Switch />
                    </Form.Item>
                  </Col>
                </Row>
              </Col>
            </Row>
          </Col>
        </Row>


        <Row gutter={24}>
          <Col xl={24} xxl={12}>
            <Typography.Title level={5}>Shelters</Typography.Title>
            <TableTransfer
              dataSource={shelterOptions}
              targetKeys={sheltersSelected}
              listStyle={null}
              leftColumns={this.transferSheltersColumns.left}
              rightColumns={this.transferSheltersColumns.right}
              onChange={this.handleShelterSelection}
              onSelectChange={(sourceSelectedKeys: string[], targetSelectedKeys: string[]) => this.handleSelectionChange('sheltersSelection', sourceSelectedKeys, targetSelectedKeys)}
            />
          </Col>

          <Col xl={24} xxl={12}>
            <Typography.Title level={5}>Sponsors</Typography.Title>
            <TableTransfer
              dataSource={sponsorOptions}
              targetKeys={sponsorsSelected}
              listStyle={null}
              leftColumns={this.transferSponsorsColumns.left}
              rightColumns={this.transferSponsorsColumns.right}
              onChange={this.handleSponsorSelection}
              onSelectChange={(sourceSelectedKeys: string[], targetSelectedKeys: string[]) => this.handleSelectionChange('sponsorsSelection', sourceSelectedKeys, targetSelectedKeys)}
            />
          </Col>
        </Row>
        
        <Form.Item style={{marginTop: 32}}>
          <Button type="primary" htmlType="submit" size="large" loading={loading} ref={(refs) => this.actionRefs.save = refs} block>
            Save
          </Button>
        </Form.Item>
      </Form>
    )
  }
}